import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DollarSign } from "lucide-react";

/** Admin-editable portfolio energy rates used for all cost figures. */
export function EnergyRatesCard({ isAdmin }) {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/energy/rates");
      setRates(data);
      setForm({
        elec_rate: data.elec_rate,
        gas_rate: data.gas_rate,
        water_rate: data.water_rate,
        currency: data.currency,
      });
    } catch (e) {
      console.warn("Could not load energy rates", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/energy/rates", {
        elec_rate: Number(form.elec_rate),
        gas_rate: Number(form.gas_rate),
        water_rate: Number(form.water_rate),
        currency: form.currency,
      });
      toast.success("Energy rates updated");
      load();
    } catch (e) {
      toast.error("Could not save rates", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!rates || !form) return null;

  const fields = [
    { key: "elec_rate", label: "Electricity", suffix: "per kWh" },
    { key: "gas_rate", label: "Gas", suffix: "per m³" },
    { key: "water_rate", label: "Water", suffix: "per gal" },
  ];

  return (
    <div className="k-card p-6" data-testid="energy-rates-card">
      <div className="k-label flex items-center gap-2">
        <DollarSign className="h-3.5 w-3.5 text-[#156F5B]" /> Energy rates
      </div>
      <p className="mt-2 text-sm text-[#6B7280]">
        One set of rates is applied portfolio-wide to turn metered usage into cost.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="k-sublabel">{f.label}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form[f.key]}
              disabled={!isAdmin}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              data-testid={`rate-input-${f.key}`}
              className="k-input mt-2 disabled:bg-[#F9FAFB] disabled:text-[#6B7280]"
            />
            <div className="mt-1 text-[11px] text-[#9CA3AF]">{f.suffix}</div>
          </div>
        ))}
        <div>
          <label className="k-sublabel">Currency</label>
          <input
            type="text"
            value={form.currency}
            disabled={!isAdmin}
            onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 8) })}
            data-testid="rate-input-currency"
            className="k-input mt-2 disabled:bg-[#F9FAFB] disabled:text-[#6B7280]"
          />
        </div>
      </div>

      {isAdmin && (
        <div className="mt-5 flex items-center gap-3">
          <button onClick={save} disabled={busy} data-testid="rates-save-btn" className="k-btn !py-2 !px-4 !text-xs">
            {busy ? "Saving…" : "Save rates"}
          </button>
          {rates.updated_by && (
            <span className="k-sublabel">
              Last updated by {rates.updated_by}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
